import {
  Box,
  Flex,
  Heading,
  Stack,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  useToast,
} from '@chakra-ui/react';
import { useContext, useEffect, useRef, useState } from 'react';
import { MdInfo, MdIntegrationInstructions, MdPalette, MdSettings, MdTune } from 'react-icons/md';
import { MarketplaceContext } from 'src/contexts/marketplace';
import useRequest from 'src/utils/hooks/useRequest';
import { MarketplaceInfo } from 'src/api/v1-api';
import { useRouter } from 'next/router';
import { CoverLoader } from 'src/components/atoms';
import { BasicInfoFormFields, BasicInfoForm } from './tabs/basic-info-form';
import { BrandingFormFields, BrandingForm } from './tabs/branding-form';
import { FeaturesFormFields, FeaturesForm } from './tabs/features-form';
import { IntegrationsFormFields, IntegrationsForm } from './tabs/integrations-form';
import { AdvancedFormFields, AdvancedForm } from './tabs/advanced-form';
import { toFormData } from 'axios';

const MarketplaceForm = () => {
  const router = useRouter();
  const toast = useToast();
  const { api } = useRequest();
  const { marketplace, setMarketplace, logoIsCircle, setLogoIsCircle } =
    useContext(MarketplaceContext);
  const { success, tab } = router.query;

  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Scroll state for tab navigation
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const tabListRef = useRef<HTMLDivElement>(null);
  const [activeTabIndex, setActiveTabIndex] = useState(0);

  useEffect(() => {
    if (tab) {
      switch (tab) {
        case 'info':
          setActiveTabIndex(0);
          break;
        case 'branding':
          setActiveTabIndex(1);
          break;
        case 'features':
          setActiveTabIndex(2);
          break;
        case 'integrations':
          setActiveTabIndex(3);
          break;
        case 'advanced':
          setActiveTabIndex(4);
          break;
        default:
          setActiveTabIndex(0);
      }
    }
  }, [tab]);

  const checkScrollPosition = () => {
    if (tabListRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = tabListRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1);
    }
  };

  useEffect(() => {
    if (
      router.isReady &&
      success &&
      success === 'Update' &&
      !toast.isActive('marketplace-update-success')
    ) {
      toast({
        title: 'Welcome to your site!',
        description:
          "You can now change any other settings before your site is visible to the public. When you're ready, click the 'Ready to launch?' button to request to go live.",
        status: 'success',
        duration: 15000,
        isClosable: true,
        id: 'marketplace-update-success',
      });
    } else if (router.isReady && success && success === 'clear') {
      // remove the success query param
      const hostname = window.location.hostname || marketplace?.domain?.domain;
      localStorage.removeItem(`marketplace-${hostname}`);
      // reload the page without the query param
      router.push('/admin/info');
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.isReady, success]);

  // Show post-save email username change notice if present
  useEffect(() => {
    if (!router.isReady) return;
    const key = marketplace?.id
      ? `email-username-changed-${marketplace.id}`
      : 'email-username-changed';
    const flag = sessionStorage.getItem(key);
    if (flag) {
      sessionStorage.removeItem(key);
      toast({
        title: 'Email sender updated',
        description:
          'We will send emails from the new email username. Ensure a mailbox or alias exists to receive replies at this address.',
        status: 'info',
        duration: 8000,
        isClosable: true,
        position: 'bottom',
      });
    }
  }, [router.isReady, marketplace?.id]);

  useEffect(() => {
    if (marketplace) {
      setIsLoading(false);
    }
  }, [marketplace]);

  useEffect(() => {
    // Check initial scroll position and add scroll listener
    const tabList = tabListRef.current;
    let resizeTimeoutId: NodeJS.Timeout | null = null;

    if (tabList) {
      checkScrollPosition();
      tabList.addEventListener('scroll', checkScrollPosition);

      // Also check on resize in case tabs change layout
      const handleResize = () => {
        // Clear any existing timeout
        if (resizeTimeoutId) {
          clearTimeout(resizeTimeoutId);
        }
        resizeTimeoutId = setTimeout(checkScrollPosition, 100); // Small delay to ensure layout is complete
      };
      window.addEventListener('resize', handleResize);

      return () => {
        tabList.removeEventListener('scroll', checkScrollPosition);
        window.removeEventListener('resize', handleResize);
        // Clear any pending timeout to prevent state updates on unmounted component
        if (resizeTimeoutId) {
          clearTimeout(resizeTimeoutId);
        }
      };
    }
  }, [marketplace]); // Re-run when marketplace changes as it might affect tab content

  const updateMarketplaceCallback = (updatedMarketplace: MarketplaceInfo) => {
    // Remove the marketplace from local storage
    const hostname = window.location.hostname || marketplace?.domain?.domain;
    localStorage.removeItem(`marketplace-${hostname}`);
    setMarketplace(updatedMarketplace);
    router.reload();
  };

  const saveBasicInfoHandler = async (data: BasicInfoFormFields) => {
    if (!marketplace) return;
    setIsSubmitting(true);
    try {
      const updatedMarketplace = await api.marketplace.update.put(
        marketplace?.id,
        toFormData(data) as FormData,
      );
      toast({
        title: 'Success.',
        description: 'Marketplace information saved successfully.',
        status: 'success',
        duration: 5000,
        isClosable: true,
        position: 'bottom',
      });
      updateMarketplaceCallback(updatedMarketplace);
    } catch (error) {
      toast({
        title: 'Error.',
        description: 'Unable to save marketplace information. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'bottom',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const saveBrandingHandler = async (data: BrandingFormFields) => {
    if (!marketplace) return;
    setIsSubmitting(true);
    try {
      const formData = new FormData();
      if (data.logoPhotoFile) {
        formData.append('image', data.logoPhotoFile);
      }
      formData.append(
        'show_total_social_media_audience',
        data.showTotalSocialmediaAudience ? 'true' : 'false',
      );
      formData.append('marketplace_theme_options', JSON.stringify(data.marketplaceThemeOptions));
      formData.append('logo_is_circle', data.logoIsCircle ? 'true' : 'false');
      formData.append('marketplace_colors', JSON.stringify(data.marketplaceColors));
      const updatedMarketplace = await api.marketplace.update.put(
        marketplace?.id,
        formData,
        !!data.logoPhotoFile,
      );
      toast({
        title: 'Success.',
        description: 'Branding information saved successfully.',
        status: 'success',
        duration: 5000,
        isClosable: true,
        position: 'bottom',
      });
      updateMarketplaceCallback(updatedMarketplace);
    } catch (error) {
      toast({
        title: 'Error.',
        description: 'Unable to save branding information. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'bottom',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const saveFeaturesHandler = async (data: FeaturesFormFields) => {
    if (!marketplace) return;
    setIsSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('collect_email_module', JSON.stringify(data.collectEmailModule));
      formData.append(
        'show_inactive_listings_info',
        data.displayInactiveListingInfo ? 'true' : 'false',
      );
      formData.append('show_shopping_cart', data.showShoppingCart ? 'true' : 'false');
      formData.append('marketplace_banner', JSON.stringify(data.marketplaceBanner));
      formData.append(
        'show_first_party_listings_first',
        data.showFirstPartyListingsFirst ? 'true' : 'false',
      );
      formData.append(
        'enable_buyer_digest_emails',
        data.enableBuyerDigestEmails ? 'true' : 'false',
      );

      const updatedMarketplace = await api.marketplace.update.put(marketplace?.id, formData);
      toast({
        title: 'Success.',
        description: 'Features information saved successfully.',
        status: 'success',
        duration: 5000,
        isClosable: true,
        position: 'bottom',
      });
      updateMarketplaceCallback(updatedMarketplace);
    } catch (error) {
      toast({
        title: 'Error.',
        description: 'Unable to save features information. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'bottom',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const saveIntegrationsHandler = async (data: IntegrationsFormFields) => {
    if (!marketplace) return;
    setIsSubmitting(true);
    try {
      const formData = new FormData();
      if (data.shortenedName !== marketplace?.shortened_name) {
        formData.append('shortened_name', data.shortenedName);
      }
      if (
        data.googleSearchConsoleVerification !==
        marketplace?.functional?.google_search_console_verification
      ) {
        formData.append('google_search_console_verification', data.googleSearchConsoleVerification);
      }
      if (data.apexDomain && data.apexDomain !== marketplace?.domain?.domain) {
        // strip http:// and https:// and any trailing slashes
        const newApexDomain = data.apexDomain
          .trim()
          .replace('http://', '')
          .replace('https://', '')
          .replace(/\/$/, '');
        if (newApexDomain !== marketplace?.domain?.domain) {
          formData.append('domain', newApexDomain);
        }
      }
      if (
        data.emailState &&
        (data.emailState.emailUsernameChangedAfterDns ||
          data.emailState.digestUsernameChangedAfterDns)
      ) {
        try {
          const currentConfig = (
            marketplace?.domain?.config ? JSON.parse(JSON.stringify(marketplace.domain.config)) : {}
          ) as any;

          if (data.emailState.emailUsernameChangedAfterDns) {
            currentConfig.email_config = currentConfig.email_config || {};
            currentConfig.email_config.email = data.emailState.supportEmail;
            // Ensure domain remains the same
            if (!currentConfig.email_config.domain) {
              currentConfig.email_config.domain =
                marketplace?.domain?.config?.email_config?.domain ||
                marketplace?.domain?.domain ||
                '';
            }
          }

          if (data.emailState.digestUsernameChangedAfterDns) {
            currentConfig.digest_config = currentConfig.digest_config || {};
            currentConfig.digest_config.email = data.emailState.digestEmail;
            if (!currentConfig.digest_config.domain) {
              currentConfig.digest_config.domain =
                marketplace?.domain?.config?.digest_config?.domain ||
                marketplace?.domain?.domain ||
                '';
            }
          }

          // Append domain to trigger backend upsert of domain + config
          if (marketplace?.domain?.domain) {
            formData.append('domain', marketplace.domain.domain);
          }
          formData.append('domain_config', JSON.stringify(currentConfig));
        } catch (_e) {
          // no-op if config missing
        }
      }
      const updatedMarketplace = await api.marketplace.update.put(marketplace.id, formData);
      toast({
        title: 'Success.',
        description: 'Integrations information saved successfully.',
        status: 'success',
        duration: 5000,
        isClosable: true,
        position: 'bottom',
      });
      // Persist a flag so we can show the alert after reload
      if (data.emailState) {
        const usernameChanged =
          data.emailState.emailUsernameChangedAfterDns ||
          data.emailState.digestUsernameChangedAfterDns;
        if (usernameChanged) {
          const key = marketplace?.id
            ? `email-username-changed-${marketplace.id}`
            : 'email-username-changed';
          sessionStorage.setItem(key, '1');
        }
      }
      updateMarketplaceCallback(updatedMarketplace);
    } catch (error) {
      toast({
        title: 'Error.',
        description: 'Unable to save integrations information. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'bottom',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const saveAdvancedHandler = async (data: AdvancedFormFields) => {
    if (!marketplace) return;
    setIsSubmitting(true);
    try {
      const formData = new FormData();
      if (data.sourceToListedVerb) {
        formData.append('source_to_listed_verb', JSON.stringify(data.sourceToListedVerb));
      }
      formData.append('default_overrides', JSON.stringify(data.defaultOverrides));
      formData.append('text_overrides', JSON.stringify(data.textOverrides));
      const updatedMarketplace = await api.marketplace.update.put(marketplace.id, formData);
      toast({
        title: 'Success.',
        description: 'Advanced information saved successfully.',
        status: 'success',
        duration: 5000,
        isClosable: true,
        position: 'bottom',
      });
      updateMarketplaceCallback(updatedMarketplace);
    } catch (error) {
      toast({
        title: 'Error.',
        description: 'Unable to save advanced information. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'bottom',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Flex direction="column" width="100%">
      <Stack justify="flex-start" align="flex-start" spacing="28px" maxWidth="100%">
        <Heading fontSize="2xl" pb={2}>
          Marketplace Info
        </Heading>

        <Box position="relative" maxWidth="100%" w="100%">
          <Tabs
            colorScheme="primary"
            size={{ base: 'md', md: 'lg' }}
            variant="enclosed"
            onChange={(i) => setActiveTabIndex(i)}
            index={activeTabIndex}
          >
            <Box position="relative">
              <TabList
                ref={tabListRef}
                overflowX="auto"
                overflowY="hidden"
                whiteSpace="nowrap"
                css={{
                  scrollbarWidth: 'none', // Firefox
                  msOverflowStyle: 'none', // IE/Edge
                  '&::-webkit-scrollbar': {
                    display: 'none', // Chrome/Safari
                  },
                }}
              >
                <Tab fontSize={{ base: 'sm', md: 'md' }} px={{ base: 2, md: 4 }}>
                  <MdInfo style={{ marginRight: '8px' }} />
                  Basic Info
                </Tab>
                <Tab fontSize={{ base: 'sm', md: 'md' }} px={{ base: 2, md: 4 }}>
                  <MdPalette style={{ marginRight: '8px' }} />
                  Branding
                </Tab>
                <Tab fontSize={{ base: 'sm', md: 'md' }} px={{ base: 2, md: 4 }}>
                  <MdSettings style={{ marginRight: '8px' }} />
                  Features
                </Tab>
                <Tab fontSize={{ base: 'sm', md: 'md' }} px={{ base: 2, md: 4 }}>
                  <MdIntegrationInstructions style={{ marginRight: '8px' }} />
                  Integrations
                </Tab>
                <Tab fontSize={{ base: 'sm', md: 'md' }} px={{ base: 2, md: 4 }}>
                  <MdTune style={{ marginRight: '8px' }} />
                  Advanced
                </Tab>
              </TabList>

              {/* Left fade indicator */}
              {canScrollLeft && (
                <Box
                  position="absolute"
                  left={0}
                  top={0}
                  bottom={0}
                  width="40px"
                  background="linear-gradient(to right, white 0%, rgba(255,255,255,0.8) 50%, transparent 100%)"
                  pointerEvents="none"
                  zIndex={1}
                />
              )}

              {/* Right fade indicator */}
              {canScrollRight && (
                <Box
                  position="absolute"
                  right={0}
                  top={0}
                  bottom={0}
                  width="40px"
                  background="linear-gradient(to left, white 0%, rgba(255,255,255,0.8) 50%, transparent 100%)"
                  pointerEvents="none"
                  zIndex={1}
                />
              )}
            </Box>

            <TabPanels>
              <TabPanel px={0} py={6}>
                <BasicInfoForm
                  marketplace={marketplace}
                  onSubmit={saveBasicInfoHandler}
                  isLoading={isSubmitting}
                />
              </TabPanel>
              <TabPanel px={0} py={6}>
                <BrandingForm
                  logoIsCircle={logoIsCircle}
                  onLogoIsCircleChange={setLogoIsCircle}
                  marketplace={marketplace}
                  onSubmit={saveBrandingHandler}
                  isLoading={isSubmitting}
                />
              </TabPanel>
              <TabPanel px={0} py={6}>
                <FeaturesForm
                  marketplace={marketplace}
                  onSubmit={saveFeaturesHandler}
                  isLoading={isSubmitting}
                />
              </TabPanel>
              <TabPanel px={0} py={6}>
                <IntegrationsForm
                  marketplace={marketplace}
                  onSubmit={saveIntegrationsHandler}
                  isLoading={isSubmitting}
                />
              </TabPanel>
              <TabPanel px={0} py={6}>
                <AdvancedForm
                  marketplace={marketplace}
                  onSubmit={saveAdvancedHandler}
                  isLoading={isSubmitting}
                />
              </TabPanel>
            </TabPanels>
          </Tabs>

          {isLoading && <CoverLoader context="MarketplaceForm:LoadingMarketplaceData" />}
        </Box>
      </Stack>
    </Flex>
  );
};

export default MarketplaceForm;
